/**
 * Owner: Masa Bayu
 * File: AdminLoginPage.tsx
 * Date:Time: 2026-10-23 11:45
 * Description: §468-E — log masuk pentadbir BUKAN log masuk awam. Krom konsol
 *   yang berbeza (perisai, panel gelap) dan hanya e-mel + kata laluan: tiada
 *   Continue with Google, tiada pemilih Reader/Publisher, tiada pautan daftar,
 *   tiada CTA kedai yang menggalakkan pembaca masuk ke kawasan operasi.
 *
 *   Gagal-keras peranan: hub mengesahkan kredential dahulu, kemudian klien
 *   memeriksa `roles`. Jika sesi yang terhasil bukan `admin`, token dibuang
 *   serta-merta dan paparan memaparkan sebabnya — laluan /admin tetap disahkan
 *   pelayan (`AdminAccessGate`), jadi pemeriksaan ini keselesaan + kebersihan
 *   sesi, bukan gerbang keselamatan.
 */

import { useState, type SyntheticEvent } from 'react';
import { loginWithPassword, logoutSession } from '../auth/hub-session';
import { normalizeAuthEmail } from '../auth/auth-email';
import { describeAuthError } from '../auth/hub-error-copy';
import { isHubAdmin } from '../auth/session-auth';
import type { AuthSessionView } from '../auth/session-types';
import { getMarketplaceLocaleProfile } from '../home/marketplace/marketplace-locale';
import { ADMIN_RESTRICTED_LABELS } from './admin-restricted-labels';
import { Icon } from './AdminIcons';
import './admin-console.css';
import './admin-restricted.css';

export interface AdminLoginPageProps {
  readonly signedInName: string | null;
  readonly onNavigate: (path: string) => void;
  readonly onSignedIn: (session: AuthSessionView) => void;
  readonly onSignOut: () => void;
}

export function AdminLoginPage({
  signedInName,
  onNavigate,
  onSignedIn,
  onSignOut,
}: AdminLoginPageProps) {
  const language = getMarketplaceLocaleProfile().language;
  const la = ADMIN_RESTRICTED_LABELS[language];
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const session = await loginWithPassword({
        email: normalizeAuthEmail(email),
        password,
      });
      setPassword('');
      if (!isHubAdmin(session)) {
        // Gagal-keras: token sesi bukan pentadbir tidak boleh tinggal di stor.
        void logoutSession();
        onSignOut();
        setError(la.loginNotAdmin);
        return;
      }
      onSignedIn(session);
      onNavigate('/admin');
    } catch (err) {
      setError(describeAuthError(err instanceof Error ? err.message : la.loginFailed));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="qxa qxa--restricted" aria-labelledby="qxa-login-title">
      <div className="qxa-login">
        <header className="qxa-login__head">
          <span className="qxa-login__mark" aria-hidden="true">
            <Icon name="shield" size={22} />
          </span>
          <div>
            <h1 id="qxa-login-title" className="qxa-login__title">
              {la.loginTitle}
            </h1>
            <p className="qxa-login__sub">{la.loginSubtitle}</p>
          </div>
          <span className="qxa-login__badge">
            <Icon name="lock" size={13} /> {la.loginBadge}
          </span>
        </header>

        {signedInName !== null ? <p className="qxa-login__note">{la.loginSessionNote}</p> : null}

        <form className="qxa-login__form" onSubmit={(event) => void onSubmit(event)}>
          <label>
            {la.loginEmail}
            <input
              name="email"
              type="email"
              autoComplete="username"
              placeholder="admin@qxread.com"
              value={email}
              required
              disabled={pending}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </label>
          <label>
            {la.loginPassword}
            <span className="qxa-login__password">
              <input
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                required
                disabled={pending}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
              <button
                type="button"
                className="qxa-login__reveal"
                aria-label={showPassword ? la.loginHide : la.loginShow}
                disabled={pending}
                onClick={() => {
                  setShowPassword((current) => !current);
                }}
              >
                {showPassword ? la.loginHide : la.loginShow}
              </button>
            </span>
          </label>
          {error !== null ? (
            <p className="qxa-login__error" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" className="qxa__btn qxa__btn--primary" disabled={pending}>
            {pending ? la.loginPending : la.loginSubmit}
          </button>
        </form>

        <p className="qxa-login__signup">{la.loginNoSignup}</p>
        <button
          type="button"
          className="qxa__btn qxa-login__back"
          onClick={() => {
            onNavigate('/');
          }}
        >
          <Icon name="arrowLeft" size={16} />
          <span>{la.loginBackStore}</span>
        </button>
      </div>
    </section>
  );
}
